// src/components/Dashboard.tsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { analyzeResumeClarity, ClarityIssue } from "../utils/resumeParser";
import { axiosInstance, isTokenExpired } from "../utils/axiosConfig";
import NotificationCenter from "./NotificationCenter";
import ConsentRenewalPrompt from "./ConsentRenewalPrompt";
import AdBanner from "./AdBanner";
import AIAssistantPanel from "./AIAssistantPanel";
import QuickTipsPanel from "./QuickTipsPanel";
import FirstRunChecklist, { FirstRunItem } from "./FirstRunChecklist";
import ProfileCompletionBanner from "./ProfileCompletionBanner";
import TourGuideModal from "./TourGuideModal";
import GlobalSearchDropdown from "./GlobalSearchDropdown";
import '../styles/DashboardNew.css';

// Sidebar nav filter helper — returns true if item should be visible given the
// current search query. Always returns true when the query is empty.
const matchesSidebarFilter = (label: string, sectionTitle: string, query: string) => {
  if (!query.trim()) return true;
  const q = query.trim().toLowerCase();
  return label.toLowerCase().includes(q) || sectionTitle.toLowerCase().includes(q);
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [showConsentPrompt, setShowConsentPrompt] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentStatus, setConsentStatus] = useState<{
    hasConsented: boolean;
    granted: boolean;
  } | null>(null);
  const [clarityIssues, setClarityIssues] = useState<ClarityIssue[]>([]);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showAssistant, setShowAssistant] = useState(false);
  const [showQuickTips, setShowQuickTips] = useState(false);
  const [clarityExpanded, setClarityExpanded] = useState(false);
  // Sidebar search: drives both local nav filtering AND the global results dropdown
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  // Anchor for the floating global-results dropdown — keeps it docked to the
  // right of the sidebar instead of overlapping the filtered nav items below.
  const sidebarSearchAnchorRef = useRef<HTMLDivElement>(null);
  const [moraleBooster, setMoraleBooster] = useState<{
    shouldShow: boolean;
    message?: string;
    state?: "active" | "prevented" | "disabled";
    reason?: string;
  } | null>(null);
  const [dashboardStats, setDashboardStats] = useState<{
    totalApplications: number;
    recommendations: number;
    successRate: number;
    interviewed: number;
    offered: number;
    avgResponseTime: string;
    topCompanies: string;
  }>({
    totalApplications: 0,
    recommendations: 0,
    successRate: 0,
    interviewed: 0,
    offered: 0,
    avgResponseTime: "N/A",
    topCompanies: "None yet",
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [currentStatCardIndex, setCurrentStatCardIndex] = useState(0);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [hasConnectedEmail, setHasConnectedEmail] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [showMfaSuggestion, setShowMfaSuggestion] = useState(false);

  // UC-1.2 Step 5.1: prompt MFA enrollment once on first dashboard load after login.
  useEffect(() => {
    if (localStorage.getItem('mfaSuggestionPending') === '1') {
      setShowMfaSuggestion(true);
      localStorage.removeItem('mfaSuggestionPending');
    }
  }, []);
  const statCards = [
    {
      icon: "📋",
      title: "Applications",
      number: statsLoading ? "—" : dashboardStats.totalApplications,
      subtitle: "Total tracked",
      cta: "View All",
      onClick: () => navigate("/applications"),
      color: "blue"
    },
    {
      icon: "⭐",
      title: "Recommendations",
      number: statsLoading ? "—" : dashboardStats.recommendations,
      subtitle: "Fresh matches",
      cta: "Explore",
      onClick: () => navigate("/recommendations"),
      color: "teal"
    },
    {
      icon: "🎯",
      title: "Interview Rate",
      number: statsLoading ? "—" : `${dashboardStats.successRate}%`,
      subtitle: "Conversion rate",
      cta: "Track",
      onClick: () => navigate("/applications"),
      color: "gradient"
    }
  ];

  // Idle timeout warning — check every 30s, warn 2 minutes before expiry
  useEffect(() => {
    const WARNING_BEFORE_MS = 2 * 60 * 1000;
    const interval = setInterval(() => {
      const expiry = localStorage.getItem("tokenExpiry");
      if (!expiry) return;
      const msLeft = parseInt(expiry) - Date.now();
      if (msLeft > 0 && msLeft <= WARNING_BEFORE_MS) {
        setShowIdleWarning(true);
      } else if (msLeft <= 0) {
        clearInterval(interval);
        localStorage.removeItem("token");
        localStorage.removeItem("tokenExpiry");
        navigate("/login");
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [navigate]);

  const handleContinueSession = async () => {
    try {
      const response = await axiosInstance.post("/auth/refresh-token");
      if (response.data?.token) {
        localStorage.setItem("token", response.data.token);
        if (response.data.tokenExpiry) {
          localStorage.setItem("tokenExpiry", String(response.data.tokenExpiry));
        }
      } else {
        // Extend locally by 30 minutes if no refresh endpoint
        const newExpiry = Date.now() + 30 * 60 * 1000;
        localStorage.setItem("tokenExpiry", String(newExpiry));
      }
    } catch {
      const newExpiry = Date.now() + 30 * 60 * 1000;
      localStorage.setItem("tokenExpiry", String(newExpiry));
    }
    setShowIdleWarning(false);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");

    // Check if token exists and is not expired
    if (!token || isTokenExpired()) {
      localStorage.removeItem("token");
      localStorage.removeItem("tokenExpiry");
      navigate("/login");
      return;
    }

    const checkConsent = async () => {
      try {
        const response = await axiosInstance.get("/consents/all");

        const allConsents = response.data;

        setConsentStatus({
          hasConsented: true,
          granted: allConsents.data_storage || false,
        });

        // Prompt only if no consent record at all (first time)
        if (
          !allConsents.data_storage &&
          Object.keys(allConsents).length === 0
        ) {
          setShowConsentPrompt(true);
        }
      } catch (err) {
        console.error("Consent check failed", err);
        setConsentStatus({ hasConsented: true, granted: false });
      }
    };

    // Fetch morale booster
    const fetchMoraleBooster = async () => {
      try {
        const response = await axiosInstance.get("/auth/morale-booster");
        setMoraleBooster(response.data);
      } catch (err) {
        console.error("Failed to fetch morale booster:", err);
      }
    };

    // Fetch dashboard stats
    const fetchDashboardStats = async () => {
      setStatsLoading(true);
      try {
        // Fetch applications
        const appResponse = await axiosInstance.get("/auth/jobs/applications");
        const applications = appResponse.data.applications || [];
        const totalApplications = applications.length;
        const interviewed = applications.filter((app: any) => app.status === "interview_scheduled").length;
        const offered = applications.filter((app: any) => app.status === "offered").length;
        const successRate = totalApplications > 0 ? Math.round((interviewed / totalApplications) * 100) : 0;

        // Avg response time from responded apps
        const respondedApps = applications.filter((app: any) =>
          app.status !== "applied" && app.appliedAt && app.updatedAt
        );
        let avgResponseTime = "N/A";
        if (respondedApps.length > 0) {
          const totalDays = respondedApps.reduce((sum: number, app: any) => {
            const diff = new Date(app.updatedAt).getTime() - new Date(app.appliedAt).getTime();
            return sum + Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
          }, 0);
          const avg = Math.round(totalDays / respondedApps.length);
          avgResponseTime = `${avg} day${avg !== 1 ? "s" : ""} avg`;
        }

        // Top companies
        const companyCounts: Record<string, number> = {};
        applications.forEach((app: any) => {
          if (app.company) companyCounts[app.company] = (companyCounts[app.company] || 0) + 1;
        });
        const topCompanyList = Object.entries(companyCounts)
          .sort((a, b) => (b[1] as number) - (a[1] as number))
          .slice(0, 3)
          .map(([company]) => company);
        const topCompanies = topCompanyList.length > 0 ? topCompanyList.join(", ") : "None yet";

        // Fetch recommendations
        const recResponse = await axiosInstance.get("/auth/jobs/recommendations");
        const recommendations = recResponse.data.recommendations ? recResponse.data.recommendations.length : 0;

        setDashboardStats({
          totalApplications,
          recommendations,
          successRate,
          interviewed,
          offered,
          avgResponseTime,
          topCompanies,
        });
      } catch (err) {
        console.error("Failed to fetch dashboard stats:", err);
      } finally {
        setStatsLoading(false);
      }
    };

    // Check consent
    checkConsent();
    // Fetch morale booster
    fetchMoraleBooster();
    // Fetch dashboard stats
    fetchDashboardStats();
    // Fetch notification count
    const fetchNotificationCount = async () => {
      try {
        const response = await axiosInstance.get("/auth/notifications", { params: { limit: 1 } });
        setUnreadNotifications(response.data.unreadCount);
      } catch (err) {
        console.error("Failed to fetch notification count:", err);
      }
    };
    fetchNotificationCount();

    // Fetch subscription/premium status + onboarding steps
    axiosInstance.get("/auth/profile").then(r => {
      setIsPremium(r.data?.subscription?.isActive === true);
      setProfileData(r.data);
    }).catch(() => {});

    // Derive email-connection state from the real accounts, not from clicking a
    // CTA — an account only counts if the backend verified it (isConnected).
    axiosInstance.get("/auth/email/accounts").then(r => {
      const accounts = r.data?.emailAccounts || [];
      setHasConnectedEmail(accounts.some((a: any) => a.isConnected));
    }).catch(() => setHasConnectedEmail(false));
  }, [navigate]);

  // Auto-rotate stat cards every 20 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStatCardIndex((prev) => (prev + 1) % 3);
    }, 20000);
    return () => clearInterval(interval);
  }, []);

  // Fetch and analyze real resume - only when consent is explicitly granted
  useEffect(() => {
    if (consentStatus?.granted === true) {
      const fetchAndAnalyzeResume = async () => {
        setResumeLoading(true);
        try {
          // Fetch real resume text from backend
          const response = await axiosInstance.get("/auth/resume/text");
          const resumeText = response.data.text;

          // Analyze the real resume for clarity issues
          const analysis = analyzeResumeClarity(resumeText);
          setClarityIssues(analysis.clarityIssues);
        } catch (err: any) {
          const errorMsg =
            err.response?.data?.error || "Could not analyze resume.";
          console.error("Resume analysis failed:", err);
          toast.error(errorMsg);
          setClarityIssues([]);
        } finally {
          setResumeLoading(false);
        }
      };
      fetchAndAnalyzeResume();
    }
  }, [consentStatus?.granted]);

  const handleConsent = async (agreed: boolean) => {
    setConsentLoading(true);
    try {
      await axiosInstance.post("/consents/data-storage", { agreed });
      setConsentStatus({ hasConsented: true, granted: agreed });
      setShowConsentPrompt(false);
      toast.success(
        agreed
          ? "Thank you! Data storage enabled."
          : "Consent declined. Some features limited.",
      );
    } catch (err) {
      toast.error("Failed to save consent. Please try again.");
    } finally {
      setConsentLoading(false);
    }
  };

  const handleMoraleOptOut = async (optOut: boolean) => {
    try {
      await axiosInstance.post("/auth/morale-booster/toggle", { optOut });
      toast.success(
        optOut ? "Morale booster disabled" : "Morale booster enabled",
      );

      // Refetch morale booster state
      const response = await axiosInstance.get("/auth/morale-booster");
      setMoraleBooster(response.data);
    } catch (err) {
      toast.error("Failed to update morale booster preference");
    }
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const handlePremiumNav = (path: string) => {
    // Locked features send the user to upgrade instead of the premium page.
    // The backend also enforces this (returns 403 upgradeRequired), so this is
    // a UX shortcut, not the security boundary.
    if (!isPremium) {
      toast(
        (t) => (
          <span>
            👑 This is a premium feature.{" "}
            <button
              onClick={() => { toast.dismiss(t.id); navigate("/subscription"); }}
              style={{ background: "#2563eb", color: "white", border: "none", borderRadius: "6px", padding: "4px 10px", cursor: "pointer", marginLeft: "4px", fontWeight: 600 }}
            >
              Upgrade
            </button>
          </span>
        ),
        { duration: 5000 }
      );
      return;
    }
    navigate(path);
  };

  const confirmLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("tokenExpiry");
    navigate("/login");
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  return (
    <div className="hb-dashboard-wrapper">

      {/* Post-signup welcome tour (one-time) */}
      <TourGuideModal />

      {/* UC-1.2 Step 5.1: MFA enrollment suggestion after login */}
      {showMfaSuggestion && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: "12px", padding: "32px", maxWidth: "440px", width: "90%", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🔐</div>
            <h3 style={{ margin: "0 0 12px", fontSize: "20px", fontWeight: 700 }}>Protect your account</h3>
            <p style={{ color: "#555", marginBottom: "24px", lineHeight: 1.5 }}>
              Two-factor authentication adds a second layer of security to your Sumbitrbuddy account.
              We strongly recommend enabling it.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button
                onClick={() => { setShowMfaSuggestion(false); navigate('/settings?tab=security'); }}
                style={{ padding: "10px 24px", background: "#1976d2", color: "#fff", border: "none", borderRadius: "6px", fontWeight: 700, cursor: "pointer", fontSize: "15px" }}
              >
                Enable MFA
              </button>
              <button
                onClick={() => setShowMfaSuggestion(false)}
                style={{ padding: "10px 20px", background: "#eee", color: "#333", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "15px" }}
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Idle Timeout Warning */}
      {showIdleWarning && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: "12px", padding: "32px", maxWidth: "420px", width: "90%", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>⏱️</div>
            <h3 style={{ margin: "0 0 12px", fontSize: "20px", fontWeight: 700 }}>Still there?</h3>
            <p style={{ color: "#555", marginBottom: "24px", lineHeight: 1.5 }}>
              Your session is about to expire due to inactivity. Press <strong>Continue</strong> to stay logged in.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button onClick={handleContinueSession} style={{ padding: "10px 28px", background: "#1976d2", color: "#fff", border: "none", borderRadius: "6px", fontWeight: 700, cursor: "pointer", fontSize: "15px" }}>
                Continue
              </button>
              <button onClick={() => { setShowIdleWarning(false); handleLogout(); }} style={{ padding: "10px 20px", background: "#eee", color: "#333", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "15px" }}>
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar Navigation */}
      <aside className="hb-dashboard-sidebar">
        <div className="hb-sidebar-header">
          <h2 className="hb-sidebar-logo">🎯 Sumbitrbuddy</h2>
        </div>

        {/* Search — filters sidebar nav AND opens a global results dropdown */}
        <div ref={sidebarSearchAnchorRef} className="hb-sidebar-search" style={{ position: "relative" }}>
          <input
            type="text"
            placeholder="Search jobs, resumes, firms, navigation..."
            className="hb-sidebar-search-input"
            value={sidebarSearch}
            onChange={(e) => setSidebarSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
            aria-label="Search Sumbitrbuddy"
          />
          {sidebarSearch.length > 0 && (
            <button
              onClick={() => { setSidebarSearch(""); setSearchFocused(false); }}
              aria-label="Clear search"
              style={{
                position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                background: "transparent", border: "none", color: "#94a3b8",
                cursor: "pointer", fontSize: 16, padding: 4, lineHeight: 1,
              }}
            >
              ×
            </button>
          )}
          {searchFocused && sidebarSearch.trim().length >= 2 && (
            <GlobalSearchDropdown
              query={sidebarSearch}
              anchorRef={sidebarSearchAnchorRef}
              onClose={() => { setSidebarSearch(""); setSearchFocused(false); }}
            />
          )}
        </div>

        {/* Navigation Sections */}
        <nav className="hb-sidebar-nav">

          {/* Sidebar nav: each section + item uses matchesSidebarFilter so the
              search input acts as a local nav filter. When the filter hides
              all items in a section, the section header is hidden too. */}

          {/* Search */}
          {matchesSidebarFilter("Search Jobs", "SEARCH", sidebarSearch) && (
            <div className="hb-sidebar-section">
              <h3 className="hb-sidebar-section-title">SEARCH</h3>
              <button onClick={() => navigate("/jobs")} className="hb-sidebar-nav-item">
                <span className="hb-sidebar-nav-icon">🔍</span>
                <span>Search Jobs</span>
              </button>
            </div>
          )}

          {/* Resume Optimization */}
          {(matchesSidebarFilter("Resume Analysis", "RESUME OPTIMIZATION", sidebarSearch) ||
            matchesSidebarFilter("AI Optimization", "RESUME OPTIMIZATION", sidebarSearch)) && (
            <div className="hb-sidebar-section">
              <h3 className="hb-sidebar-section-title">RESUME OPTIMIZATION</h3>
              {matchesSidebarFilter("Resume Analysis", "RESUME OPTIMIZATION", sidebarSearch) && (
                <button onClick={() => handlePremiumNav("/resume-analysis")} className="hb-sidebar-nav-item">
                  <span className="hb-sidebar-nav-icon">📊</span>
                  <span>Resume Analysis</span>
                  {!isPremium && <span style={{ marginLeft: "auto", fontSize: "12px" }} title="Premium feature">👑</span>}
                </button>
              )}
              {matchesSidebarFilter("AI Optimization", "RESUME OPTIMIZATION", sidebarSearch) && (
                <button onClick={() => handlePremiumNav("/ai-optimization")} className="hb-sidebar-nav-item">
                  <span className="hb-sidebar-nav-icon">✨</span>
                  <span>AI Optimization</span>
                  {!isPremium && <span style={{ marginLeft: "auto", fontSize: "12px" }} title="Premium feature">👑</span>}
                </button>
              )}
            </div>
          )}

          {/* Jobs */}
          {(matchesSidebarFilter("AI Job Title Recommender", "JOBS", sidebarSearch) ||
            matchesSidebarFilter("Search Job Boards", "JOBS", sidebarSearch) ||
            matchesSidebarFilter("Saved Jobs", "JOBS", sidebarSearch) ||
            matchesSidebarFilter("Analytics", "JOBS", sidebarSearch)) && (
            <div className="hb-sidebar-section">
              <h3 className="hb-sidebar-section-title">JOBS</h3>
              {matchesSidebarFilter("AI Job Title Recommender", "JOBS", sidebarSearch) && (
                <button onClick={() => handlePremiumNav("/ai-matches")} className="hb-sidebar-nav-item">
                  <span className="hb-sidebar-nav-icon">🤖</span>
                  <span>AI Job Title Recommender</span>
                  {!isPremium && <span style={{ marginLeft: "auto", fontSize: "12px" }} title="Premium feature">👑</span>}
                </button>
              )}
              {matchesSidebarFilter("Search Job Boards", "JOBS", sidebarSearch) && (
                <button onClick={() => navigate("/job-search")} className="hb-sidebar-nav-item">
                  <span className="hb-sidebar-nav-icon">💼</span>
                  <span>Search Job Boards</span>
                </button>
              )}
              {matchesSidebarFilter("Saved Jobs", "JOBS", sidebarSearch) && (
                <button onClick={() => navigate("/saved-jobs")} className="hb-sidebar-nav-item">
                  <span className="hb-sidebar-nav-icon">📌</span>
                  <span>Saved Jobs</span>
                </button>
              )}
              {matchesSidebarFilter("Analytics", "JOBS", sidebarSearch) && (
                <button onClick={() => handlePremiumNav("/analytics")} className="hb-sidebar-nav-item">
                  <span className="hb-sidebar-nav-icon">📈</span>
                  <span>Analytics</span>
                  {!isPremium && <span style={{ marginLeft: "auto", fontSize: "12px" }} title="Premium feature">👑</span>}
                </button>
              )}
            </div>
          )}

          {/* Application Tracker */}
          {matchesSidebarFilter("Track Applications", "APPLICATION TRACKER", sidebarSearch) && (
            <div className="hb-sidebar-section">
              <h3 className="hb-sidebar-section-title">APPLICATION TRACKER</h3>
              <button onClick={() => navigate("/applications")} className="hb-sidebar-nav-item">
                <span className="hb-sidebar-nav-icon">📋</span>
                <span>Track Applications</span>
              </button>
            </div>
          )}

          {/* Interview Prep */}
          {matchesSidebarFilter("Interview Prep", "INTERVIEW PREP", sidebarSearch) && (
            <div className="hb-sidebar-section">
              <h3 className="hb-sidebar-section-title">INTERVIEW PREP</h3>
              <button onClick={() => handlePremiumNav("/interview-prep")} className="hb-sidebar-nav-item">
                <span className="hb-sidebar-nav-icon">🎤</span>
                <span>Interview Prep</span>
                {!isPremium && <span style={{ marginLeft: "auto", fontSize: "12px" }} title="Premium feature">👑</span>}
              </button>
            </div>
          )}

          {/* AI Assistant — available on ALL plans (product decision 2026-07-14) */}
          {matchesSidebarFilter("AI Assistant", "AI ASSISTANT", sidebarSearch) && (
            <div className="hb-sidebar-section">
              <h3 className="hb-sidebar-section-title">AI ASSISTANT</h3>
              <button
                onClick={() => setShowAssistant(true)}
                className="hb-sidebar-nav-item"
              >
                <span className="hb-sidebar-nav-icon">🧠</span>
                <span>AI Assistant</span>
              </button>
            </div>
          )}

          {/* HR / Staffing Agency */}
          {matchesSidebarFilter("Connect with Agencies", "HR / STAFFING AGENCY", sidebarSearch) && (
            <div className="hb-sidebar-section">
              <h3 className="hb-sidebar-section-title">HR / STAFFING AGENCY</h3>
              <button onClick={() => navigate("/staffing-firms")} className="hb-sidebar-nav-item">
                <span className="hb-sidebar-nav-icon">🤝</span>
                <span>Connect with Agencies</span>
              </button>
            </div>
          )}

          {/* Advanced Features */}
          {matchesSidebarFilter("Email Monitoring", "ADVANCED FEATURES", sidebarSearch) && (
            <div className="hb-sidebar-section">
              <h3 className="hb-sidebar-section-title">ADVANCED FEATURES</h3>
              <button onClick={() => handlePremiumNav("/email-accounts")} className="hb-sidebar-nav-item">
                <span className="hb-sidebar-nav-icon">📧</span>
                <span>Email Monitoring</span>
                {!isPremium && <span style={{ fontSize: "12px", marginLeft: "auto" }} title="Premium feature">👑</span>}
                <span style={{ fontSize: "10px", color: "#888", display: "block", width: "100%", whiteSpace: "nowrap" }}>Auto-tracks applications</span>
              </button>
            </div>
          )}

          {/* Community */}
          {matchesSidebarFilter("Community", "COMMUNITY", sidebarSearch) && (
            <div className="hb-sidebar-section">
              <h3 className="hb-sidebar-section-title">COMMUNITY</h3>
              <button onClick={() => navigate("/community")} className="hb-sidebar-nav-item">
                <span className="hb-sidebar-nav-icon">👥</span>
                <span>Community</span>
              </button>
            </div>
          )}

          {/* Account */}
          {(matchesSidebarFilter("Profile", "ACCOUNT", sidebarSearch) ||
            matchesSidebarFilter("Subscription & Billing", "ACCOUNT", sidebarSearch) ||
            matchesSidebarFilter("Settings", "ACCOUNT", sidebarSearch) ||
            matchesSidebarFilter("Logout", "ACCOUNT", sidebarSearch)) && (
            <div className="hb-sidebar-section">
              <h3 className="hb-sidebar-section-title">ACCOUNT</h3>
              {matchesSidebarFilter("Profile", "ACCOUNT", sidebarSearch) && (
                <button onClick={() => navigate("/profile")} className="hb-sidebar-nav-item">
                  <span className="hb-sidebar-nav-icon">👤</span>
                  <span>Profile</span>
                </button>
              )}
              {matchesSidebarFilter("Subscription & Billing", "ACCOUNT", sidebarSearch) && (
                <button onClick={() => navigate("/subscription")} className="hb-sidebar-nav-item">
                  <span className="hb-sidebar-nav-icon">💳</span>
                  <span>Subscription & Billing</span>
                </button>
              )}
              {matchesSidebarFilter("Settings", "ACCOUNT", sidebarSearch) && (
                <button onClick={() => navigate("/settings")} className="hb-sidebar-nav-item">
                  <span className="hb-sidebar-nav-icon">⚙️</span>
                  <span>Settings</span>
                </button>
              )}
              {matchesSidebarFilter("Logout", "ACCOUNT", sidebarSearch) && (
                <button onClick={handleLogout} className="hb-sidebar-nav-item">
                  <span className="hb-sidebar-nav-icon">🚪</span>
                  <span>Logout</span>
                </button>
              )}
            </div>
          )}

          {/* Empty-state when sidebar filter matches nothing */}
          {sidebarSearch.trim().length > 0 &&
            !matchesSidebarFilter("Search Jobs", "SEARCH", sidebarSearch) &&
            !matchesSidebarFilter("Resume Analysis", "RESUME OPTIMIZATION", sidebarSearch) &&
            !matchesSidebarFilter("AI Optimization", "RESUME OPTIMIZATION", sidebarSearch) &&
            !matchesSidebarFilter("AI Job Title Recommender", "JOBS", sidebarSearch) &&
            !matchesSidebarFilter("Search Job Boards", "JOBS", sidebarSearch) &&
            !matchesSidebarFilter("Saved Jobs", "JOBS", sidebarSearch) &&
            !matchesSidebarFilter("Analytics", "JOBS", sidebarSearch) &&
            !matchesSidebarFilter("Track Applications", "APPLICATION TRACKER", sidebarSearch) &&
            !matchesSidebarFilter("Interview Prep", "INTERVIEW PREP", sidebarSearch) &&
            !matchesSidebarFilter("AI Assistant", "AI ASSISTANT", sidebarSearch) &&
            !matchesSidebarFilter("Connect with Agencies", "HR / STAFFING AGENCY", sidebarSearch) &&
            !matchesSidebarFilter("Email Monitoring", "ADVANCED FEATURES", sidebarSearch) &&
            !matchesSidebarFilter("Community", "COMMUNITY", sidebarSearch) &&
            !matchesSidebarFilter("Profile", "ACCOUNT", sidebarSearch) &&
            !matchesSidebarFilter("Subscription & Billing", "ACCOUNT", sidebarSearch) &&
            !matchesSidebarFilter("Settings", "ACCOUNT", sidebarSearch) &&
            !matchesSidebarFilter("Logout", "ACCOUNT", sidebarSearch) && (
              <div style={{ padding: 16, color: "#94a3b8", fontSize: 12, textAlign: "center" }}>
                No nav items match "{sidebarSearch}". See the global results panel above.
              </div>
            )}

        </nav>
      </aside>

      {/* Main Content */}
      <main className="hb-dashboard-content">
        {/* Profile completion banner — auto-hides when profile is complete */}
        <ProfileCompletionBanner />

        {/* Top Header with Welcome */}
        <div className="hb-dashboard-top-header">
          <div className="hb-dashboard-top-left">
            <h1 className="hb-dashboard-welcome-title">Welcome back! 👋</h1>
            <p className="hb-dashboard-welcome-subtitle">Your job search, supported every step of the way.</p>
          </div>
          <div className="hb-dashboard-top-right">
            {moraleBooster?.state === "disabled" && (
              <button 
                onClick={() => handleMoraleOptOut(false)}
                className="hb-header-btn"
                title="Enable morale booster"
              >
                <span>✨</span> Enable Morale Booster
              </button>
            )}
          </div>
        </div>

        {/* Fixed Top Right Button Group */}
        <div className="hb-top-right-buttons">
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            className="hb-header-btn hb-notification-btn"
          >
            <span>🔔</span>
            {unreadNotifications > 0 && (
              <span className="hb-notification-badge">{unreadNotifications}</span>
            )}
          </button>
          <button className="hb-header-btn" onClick={() => { setShowQuickTips(false); setShowAnalytics(false); setShowAssistant(true); }}>
            <span>🤖</span> AI Assistant
          </button>
          <button className="hb-header-btn" onClick={() => { setShowAssistant(false); setShowAnalytics(false); setShowQuickTips(true); }}>
            <span>⚡</span> Quick Tips
          </button>
          <button
            onClick={() => { setShowAssistant(false); setShowQuickTips(false); setShowAnalytics(!showAnalytics); }}
            className="hb-header-btn"
          >
            <span>📊</span> Analytics
          </button>
        </div>

        {/* Notification Center */}
        <NotificationCenter
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          onUnreadCountChange={setUnreadNotifications}
        />

        {/* AI Assistant Panel */}
        <AIAssistantPanel isOpen={showAssistant} onClose={() => setShowAssistant(false)} />

        {/* Quick Tips Panel */}
        <QuickTipsPanel isOpen={showQuickTips} onClose={() => setShowQuickTips(false)} />

        {/* Consent Renewal Prompt */}
        <ConsentRenewalPrompt />

        {/* Ad Banner for Free Users */}
        <AdBanner autoRotateInterval={5000} />

        {/* Onboarding checklist — shown until all steps complete */}
        {(() => {
          const checklistItems: FirstRunItem[] = [
            {
              id: "upload_resume",
              label: "Upload your resume",
              done: !!profileData?.primaryResume,
              ctaLabel: "Upload",
              onCta: () => navigate("/resumes"),
            },
            {
              id: "set_location",
              label: "Set your job search location",
              done: !!profileData?.jobSearchLocation?.country,
              ctaLabel: "Go to Profile",
              onCta: () => navigate("/profile"),
            },
            {
              id: "connect_email",
              label: "Connect an email account for auto-tracking",
              done: hasConnectedEmail,
              ctaLabel: "Connect",
              onCta: () => navigate("/email-accounts"),
            },
            {
              id: "first_application",
              label: "Track your first job application",
              done: dashboardStats.totalApplications > 0,
              ctaLabel: "Search Jobs",
              onCta: () => navigate("/jobs"),
            },
          ];
          const allDone = checklistItems.every(i => i.done);
          if (allDone) return null;
          return (
            <FirstRunChecklist
              items={checklistItems}
              title="Get started with Sumbitrbuddy"
            />
          );
        })()}

        {/* Morale Booster Card */}
        {moraleBooster && moraleBooster?.state !== "disabled" && (
          <div className="hb-dashboard-featured-card">
            <div className="hb-featured-card-icon">✨</div>
            <div className="hb-featured-card-content">
              <h3>{moraleBooster.message || "You're doing amazing! Keep going!"}</h3>
              <button 
                onClick={() => handleMoraleOptOut(true)}
                className="hb-btn hb-btn-sm hb-btn-outline"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Analytics Panel — fixed popup matching QuickTipsPanel */}
        {showAnalytics && (
          <>
            <div
              onClick={() => setShowAnalytics(false)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.15)",
                zIndex: 998,
              }}
            />
            <aside
              style={{
                position: "fixed",
                top: 80,
                right: 24,
                width: "min(380px, calc(100vw - 48px))",
                maxHeight: "calc(100vh - 120px)",
                background: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
                zIndex: 999,
                display: "flex",
                flexDirection: "column",
                color: "#1f2937",
                overflow: "hidden",
              }}
              role="dialog"
              aria-label="Analytics"
            >
              {/* Header */}
              <header
                style={{
                  padding: "14px 18px",
                  borderBottom: "1px solid #e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>📊 Your Analytics</div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                    Live snapshot of your job search
                  </div>
                </div>
                <button
                  onClick={() => setShowAnalytics(false)}
                  aria-label="Close analytics"
                  style={{
                    background: "transparent",
                    border: "none",
                    fontSize: 20,
                    color: "#6b7280",
                    cursor: "pointer",
                    padding: 4,
                  }}
                >
                  ×
                </button>
              </header>

              {/* Stats */}
              <div style={{ overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { label: "Total Applications", value: dashboardStats.totalApplications, icon: "📋", color: "#2563eb" },
                  { label: "Interviews Scheduled", value: dashboardStats.interviewed, icon: "🎤", color: "#7c3aed" },
                  { label: "Offers Received", value: dashboardStats.offered, icon: "🏆", color: "#059669" },
                  { label: "Interview Rate", value: `${dashboardStats.successRate}%`, icon: "🎯", color: "#d97706" },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderLeft: `3px solid ${stat.color}`,
                      borderRadius: 8,
                      padding: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background: "#ffffff",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 18 }}>{stat.icon}</span>
                      <span style={{ fontSize: 13, color: "#6b7280" }}>{stat.label}</span>
                    </div>
                    <span style={{ fontSize: 22, fontWeight: 700, color: stat.color }}>
                      {statsLoading ? "—" : stat.value}
                    </span>
                  </div>
                ))}

                {/* Deep analytics link */}
                <button
                  onClick={() => { setShowAnalytics(false); handlePremiumNav("/analytics"); }}
                  style={{
                    marginTop: 4,
                    background: "#2563eb",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: 8,
                    padding: "10px 16px",
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  View Full Analytics →
                </button>
              </div>
            </aside>
          </>
        )}

        {/* Dashboard Stats Carousel */}
        <div className="hb-stats-carousel">
          <button 
            onClick={() => setCurrentStatCardIndex((prev) => (prev - 1 + 3) % 3)}
            className="hb-carousel-nav hb-carousel-prev"
            title="Previous"
          >
            ←
          </button>

          <div className="hb-carousel-track">
            {statCards.map((card, index) => (
              <div
                key={index}
                className={`hb-stat-card-carousel ${index === currentStatCardIndex ? "active" : ""}`}
              >
                <div className="hb-stat-card-left">
                  <h3>{card.icon} {card.title}</h3>
                  <div className="hb-stat-carousel-number">{card.number}</div>
                  <p>{card.subtitle}</p>
                  <button onClick={card.onClick} className="hb-stat-carousel-btn">
                    {card.cta}
                  </button>
                </div>

                <div className="hb-stat-card-right">
                  {index === 0 && (
                    <svg viewBox="0 0 200 150" preserveAspectRatio="xMidYMid meet" className="hb-carousel-chart">
                      <defs>
                        <linearGradient id="barGradient1" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#667eea"/>
                          <stop offset="100%" stopColor="#4f46e5"/>
                        </linearGradient>
                        <linearGradient id="barGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#14B8A6"/>
                          <stop offset="100%" stopColor="#0d9488"/>
                        </linearGradient>
                      </defs>
                      {/* Grid lines */}
                      <line x1="15" y1="30" x2="185" y2="30" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4"/>
                      <line x1="15" y1="60" x2="185" y2="60" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4"/>
                      <line x1="15" y1="90" x2="185" y2="90" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4"/>
                      <line x1="15" y1="120" x2="185" y2="120" stroke="#e5e7eb" strokeWidth="1"/>
                      {/* Bars with minimum height of 15 */}
                      <rect x="25" y={120 - Math.max(15, Math.min(dashboardStats.totalApplications * 2, 90))} width="28" height={Math.max(15, Math.min(dashboardStats.totalApplications * 2, 90))} fill="url(#barGradient1)" rx="4" opacity="0.7"/>
                      <rect x="60" y={120 - Math.max(20, Math.min(dashboardStats.totalApplications * 2.5, 90))} width="28" height={Math.max(20, Math.min(dashboardStats.totalApplications * 2.5, 90))} fill="url(#barGradient1)" rx="4" opacity="0.85"/>
                      <rect x="95" y={120 - Math.max(25, Math.min(dashboardStats.interviewed * 8, 90))} width="28" height={Math.max(25, Math.min(dashboardStats.interviewed * 8, 90))} fill="url(#barGradient2)" rx="4"/>
                      <rect x="130" y={120 - Math.max(18, Math.min(dashboardStats.offered * 12, 90))} width="28" height={Math.max(18, Math.min(dashboardStats.offered * 12, 90))} fill="url(#barGradient2)" rx="4" opacity="0.9"/>
                      {/* Labels */}
                      <text x="39" y="138" fontSize="8" fill="#64748b" textAnchor="middle">Apps</text>
                      <text x="74" y="138" fontSize="8" fill="#64748b" textAnchor="middle">Active</text>
                      <text x="109" y="138" fontSize="8" fill="#64748b" textAnchor="middle">Int.</text>
                      <text x="144" y="138" fontSize="8" fill="#64748b" textAnchor="middle">Offers</text>
                    </svg>
                  )}
                  {index === 1 && (
                    <svg viewBox="0 0 200 150" preserveAspectRatio="xMidYMid meet" className="hb-carousel-chart">
                      <defs>
                        <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#14B8A6"/>
                          <stop offset="100%" stopColor="#0d9488"/>
                        </linearGradient>
                      </defs>
                      {/* Background ring */}
                      <circle cx="100" cy="75" r="50" fill="none" stroke="#e5e7eb" strokeWidth="14"/>
                      {/* Progress ring - always show at least 20% */}
                      <circle cx="100" cy="75" r="50" fill="none" stroke="url(#ringGradient)" strokeWidth="14" strokeLinecap="round" strokeDasharray={`${Math.max(60, (dashboardStats.recommendations / Math.max(dashboardStats.recommendations + 5, 1)) * 314)} 314`} strokeDashoffset="0" transform="rotate(-90 100 75)" style={{transition: 'stroke-dasharray 0.6s ease-out'}}/>
                      {/* Center text */}
                      <text x="100" y="70" fontSize="24" fontWeight="700" fill="#0f172a" textAnchor="middle">{dashboardStats.recommendations}</text>
                      <text x="100" y="88" fontSize="10" fill="#64748b" textAnchor="middle">matches</text>
                    </svg>
                  )}
                  {index === 2 && (
                    <svg viewBox="0 0 200 150" preserveAspectRatio="xMidYMid meet" className="hb-carousel-chart">
                      <defs>
                        <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#fbbf24"/>
                          <stop offset="50%" stopColor="#667eea"/>
                          <stop offset="100%" stopColor="#14B8A6"/>
                        </linearGradient>
                      </defs>
                      {/* Gauge background arc */}
                      <path d="M 30 100 A 70 70 0 0 1 170 100" fill="none" stroke="#e5e7eb" strokeWidth="12" strokeLinecap="round"/>
                      {/* Gauge progress - always show at least 15% */}
                      <path d="M 30 100 A 70 70 0 0 1 170 100" fill="none" stroke="url(#gaugeGradient)" strokeWidth="12" strokeLinecap="round" strokeDasharray={`${Math.max(33, (dashboardStats.successRate / 100) * 220)} 220`} style={{transition: 'stroke-dasharray 0.6s ease-out'}}/>
                      {/* Center indicator */}
                      <circle cx="100" cy="105" r="8" fill="#667eea"/>
                      <text x="100" y="75" fontSize="28" fontWeight="700" fill="#0f172a" textAnchor="middle">{dashboardStats.successRate}%</text>
                      <text x="100" y="130" fontSize="10" fill="#64748b" textAnchor="middle">success rate</text>
                    </svg>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button 
            onClick={() => setCurrentStatCardIndex((prev) => (prev + 1) % 3)}
            className="hb-carousel-nav hb-carousel-next"
            title="Next"
          >
            →
          </button>

          <div className="hb-carousel-dots">
            {statCards.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStatCardIndex(index)}
                className={`hb-carousel-dot ${index === currentStatCardIndex ? "active" : ""}`}
                title={`Go to card ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Quick Actions & Tips Row */}
        <div className="hb-dashboard-quick-actions">
          <div className="hb-action-card">
            <h4>🚀 Next Step</h4>
            <p>
              {statsLoading
                ? "Loading your activity..."
                : dashboardStats.totalApplications === 0
                ? "Start your job search — add your first application to track your progress."
                : dashboardStats.interviewed > 0
                ? `${dashboardStats.interviewed} interview${dashboardStats.interviewed !== 1 ? "s" : ""} in progress — prepare thoroughly to convert them to offers.`
                : `${dashboardStats.totalApplications} application${dashboardStats.totalApplications !== 1 ? "s" : ""} tracked — keep the pipeline active with fresh applications.`}
            </p>
            <button onClick={() => navigate("/applications")} className="hb-action-btn">
              Check Applications
            </button>
          </div>

          <div className="hb-action-card">
            <h4>⭐ Opportunity</h4>
            <p>
              {statsLoading
                ? "Fetching your matches..."
                : dashboardStats.recommendations > 0
                ? `${dashboardStats.recommendations} personalised match${dashboardStats.recommendations !== 1 ? "es" : ""} waiting — your next interview could be one click away.`
                : "No fresh matches yet — check back to discover roles aligned with your profile."}
            </p>
            <button onClick={() => navigate("/recommendations")} className="hb-action-btn">
              Explore Matches
            </button>
          </div>

          <div className="hb-action-card">
            <h4>💼 Strength</h4>
            <p>
              {statsLoading
                ? "Calculating your rate..."
                : dashboardStats.successRate >= 30
                ? `${dashboardStats.successRate}% interview rate — you're converting well above average. Keep targeting the right roles.`
                : dashboardStats.totalApplications === 0
                ? "Apply to roles that match your profile. A targeted application beats a generic one every time."
                : "Tailor each application to the job description — it significantly improves your callback rate."}
            </p>
            <button onClick={() => navigate("/profile")} className="hb-action-btn">
              View Profile
            </button>
          </div>

          <div className="hb-action-card">
            <h4>👥 Community</h4>
            <p>Connect with fellow job seekers. Share tips, ask questions, and grow your network.</p>
            <button onClick={() => navigate("/community")} className="hb-action-btn">
              Join Discussions
            </button>
          </div>
        </div>

        {/* Resume Clarity Section */}
        {consentStatus?.granted ? (
          <div className="hb-dashboard-insights-row">
            <div className="hb-insights-card">
              <div className="hb-insights-header">
                <h3>📝 Resume Clarity</h3>
                <span className="hb-insights-badge">{clarityIssues.length === 0 ? "✅ Strong" : `${clarityIssues.length} Tips`}</span>
              </div>
              {resumeLoading ? (
                <div className="hb-clarity-loading">
                  <p>Analyzing your resume...</p>
                </div>
              ) : clarityIssues.length === 0 ? (
                <div className="hb-clarity-success">
                  <p><strong>All clear!</strong> Your resume passed every clarity check. Keep it current as you add new achievements.</p>
                </div>
              ) : (
                <>
                  <p className="hb-insights-intro">
                    Small improvements can make a big difference:
                  </p>
                  <div className="hb-clarity-issues-horizontal">
                    {(clarityExpanded ? clarityIssues : clarityIssues.slice(0, 2)).map((issue, index) => (
                      <div key={index} className="hb-clarity-issue-item">
                        <p className="hb-clarity-issue-description">{issue.description}</p>
                        <p className="hb-clarity-issue-suggestion">→ {issue.suggestion}</p>
                      </div>
                    ))}
                  </div>
                  {clarityIssues.length > 2 && (
                    <button
                      onClick={() => setClarityExpanded(prev => !prev)}
                      style={{
                        background: "none",
                        border: "none",
                        padding: "4px 0",
                        fontSize: "0.82rem",
                        color: "#2196f3",
                        cursor: "pointer",
                        textDecoration: "underline",
                        textAlign: "left",
                      }}
                    >
                      {clarityExpanded
                        ? "Show fewer suggestions ▲"
                        : `...and ${clarityIssues.length - 2} more suggestion${clarityIssues.length - 2 !== 1 ? "s" : ""} ▼`}
                    </button>
                  )}
                </>
              )}
              <button onClick={() => navigate("/profile")} className="hb-insights-cta">
                Update Resume →
              </button>
            </div>

            <div className="hb-insights-card">
              <div className="hb-insights-header">
                <h3>📊 Job Market Insight</h3>
                <span className="hb-insights-badge">Active</span>
              </div>
              <p className="hb-insights-intro">
                Based on your applications:
              </p>
              <div className="hb-market-stats">
                <div className="hb-market-stat">
                  <span className="hb-market-label">Response Time</span>
                  <span className="hb-market-value">{statsLoading ? "..." : dashboardStats.avgResponseTime}</span>
                </div>
                <div className="hb-market-stat">
                  <span className="hb-market-label">Most Applied To</span>
                  <span className="hb-market-value">{statsLoading ? "..." : dashboardStats.topCompanies}</span>
                </div>
              </div>
              <p className="hb-market-note">
                {statsLoading
                  ? "Loading your insights..."
                  : dashboardStats.totalApplications === 0
                  ? "💡 Start applying to see your personal job market insights here."
                  : dashboardStats.successRate >= 20
                  ? `💡 ${dashboardStats.successRate}% interview rate — your applications are getting noticed!`
                  : "💡 Track more applications to reveal patterns in your job search."}
              </p>
              <button onClick={() => navigate("/recommendations")} className="hb-insights-cta">
                View All Insights →
              </button>
            </div>

            <div className="hb-insights-card">
              <div className="hb-insights-header">
                <h3>🎯 Your Edge</h3>
                <span className="hb-insights-badge">{dashboardStats.successRate}% Interview Rate</span>
              </div>
              <p className="hb-insights-intro">
                {dashboardStats.successRate >= 50 ? "You're converting well—better than average!" : "You're building your track record. Each application is practice."}
              </p>
              <div className="hb-edge-content">
                <p>
                  {dashboardStats.interviewed > 0 
                    ? `You've secured ${dashboardStats.interviewed} interview${dashboardStats.interviewed !== 1 ? 's' : ''}. That takes real skill.`
                    : "Interviews are coming—stay persistent and keep improving."
                  }
                </p>
              </div>
              <button onClick={() => navigate("/applications")} className="hb-insights-cta">
                Track Progress →
              </button>
            </div>
          </div>
        ) : (
          <div className="hb-dashboard-consent-warning">
            <h3>⚠️ Limited Features</h3>
            <p>
              History, analytics, and saved preferences are currently disabled because you declined data storage.
            </p>
            <p>
              To enable full features, go to{" "}
              <a href="/settings">Settings → Privacy & Data</a>{" "}
              and enable Data Storage.
            </p>
          </div>
        )}

        {/* Consent Prompt Modal */}
        {showConsentPrompt && (
          <div className="hb-modal-overlay">
            <div className="hb-consent-modal">
              <h3>Privacy & Data Storage</h3>
              <p>To provide you with the best experience, we need your permission to store:</p>
              <ul>
                <li>Profile information (name, contact details, etc.)</li>
                <li>Application history & analytics</li>
                <li>Search filters & preferences</li>
              </ul>
              <p><strong>Required for full functionality.</strong></p>
              <div className="hb-consent-modal-actions">
                <button
                  onClick={() => handleConsent(true)}
                  disabled={consentLoading}
                  className="hb-btn hb-btn-primary"
                >
                  {consentLoading ? 'Saving...' : 'Accept'}
                </button>
                <button
                  onClick={() => handleConsent(false)}
                  disabled={consentLoading}
                  className="hb-btn hb-btn-outline"
                >
                  Decline
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Logout Confirmation Modal */}
        {showLogoutModal && (
          <div className="hb-modal-overlay">
            <div className="hb-logout-modal">
              <h3>See you soon?</h3>
              <p>Are you sure you want to log out?</p>
              <div className="hb-logout-modal-actions">
                <button
                  onClick={confirmLogout}
                  className="hb-btn hb-btn-primary"
                >
                  Yes, Log Out
                </button>
                <button
                  onClick={cancelLogout}
                  className="hb-btn hb-btn-outline"
                >
                  Stay Logged In
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
